import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Privacy Policy - WorkNation Labour Ltd</title>
        <meta name="description" content="WorkNation Labour Ltd privacy policy. Learn how we collect, use, and protect your personal information in accordance with PIPEDA and BC privacy laws." />
        <link rel="canonical" href="https://worknation.ca/privacy-policy" />
      </Helmet>
      <Header />
      <main className="pt-32 pb-20 px-6">
        <article className="max-w-4xl mx-auto prose prose-lg prose-slate">
          <h1 className="text-4xl font-bold text-[#003366] mb-2">Privacy Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: {new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <p className="text-gray-700 leading-relaxed">
            WorkNation Labour Ltd (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our staffing services.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-10 mb-4">1. Information We Collect</h2>
          <p className="text-gray-700 mb-4">We may collect the following types of personal information:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li><strong>Contact Information:</strong> Name, email address, phone number, and mailing address.</li>
            <li><strong>Employment Information:</strong> Resume, work experience, certifications, skills, and availability.</li>
            <li><strong>Business Information:</strong> Company name, industry, and staffing requirements (for employers).</li>
            <li><strong>Website Usage Data:</strong> IP address, browser type, pages visited, and interaction data collected through cookies and analytics tools.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-10 mb-4">2. How We Use Your Information</h2>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>To match job seekers with employment opportunities.</li>
            <li>To process staffing requests from employers.</li>
            <li>To communicate with you about services, job opportunities, and inquiries.</li>
            <li>To administer payroll, WCB coverage, and employment-related services.</li>
            <li>To improve our website and services.</li>
            <li>To comply with legal obligations.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-10 mb-4">3. How We Share Your Information</h2>
          <p className="text-gray-700 mb-4">We may share your personal information with:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li><strong>Employer Partners:</strong> Your qualifications and contact information may be shared with employers for job matching purposes.</li>
            <li><strong>Service Providers:</strong> Third-party providers who assist with payroll, IT, and business operations.</li>
            <li><strong>Government Authorities:</strong> When required by law, such as for WCB reporting or tax purposes.</li>
          </ul>
          <p className="text-gray-700 mt-2">We do not sell your personal information to third parties.</p>

          <h2 className="text-2xl font-bold text-[#003366] mt-10 mb-4">4. Cookies and Analytics</h2>
          <p className="text-gray-700">
            We use Google Analytics to understand how visitors interact with our website. This service collects anonymized data including pages visited, time spent on site, and referral sources. You can opt out of Google Analytics by installing the Google Analytics Opt-Out Browser Add-on.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-10 mb-4">5. Data Security</h2>
          <p className="text-gray-700">
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of electronic storage or transmission is 100% secure.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-10 mb-4">6. Your Rights</h2>
          <p className="text-gray-700 mb-4">Under the Personal Information Protection and Electronic Documents Act (PIPEDA) and BC&apos;s Personal Information Protection Act (PIPA), you have the right to:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Access the personal information we hold about you.</li>
            <li>Request correction of inaccurate information.</li>
            <li>Request deletion of your personal information.</li>
            <li>Withdraw consent for the collection and use of your data.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-10 mb-4">7. Data Retention</h2>
          <p className="text-gray-700">
            We retain personal information only as long as necessary to fulfill the purposes for which it was collected, or as required by law. Employment records are retained in accordance with applicable Canadian employment laws.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-10 mb-4">8. Contact Us</h2>
          <p className="text-gray-700">
            If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
          </p>
          <ul className="list-none pl-0 text-gray-700 space-y-1 mt-2">
            <li><strong>Email:</strong> <a href="mailto:info@worknation.ca" className="text-[#003366] hover:underline">info@worknation.ca</a></li>
            <li><strong>Location:</strong> Vancouver, British Columbia, Canada</li>
          </ul>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
